import ErnestMascot from '@/components/ErnestMascot';
import SelectSubjectModal from '@/components/SelectSubjectModal';
import { useCoachConversations } from '@/hooks/useCoachConversations';
import { useUserProfile } from '@/hooks/useUserProfile';
import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';
import { CaretRight, ChatCircleDots, Clock, GraduationCap, Plus, Sparkle, Trash } from 'phosphor-react-native';
import { useState } from 'react';
import { Alert, Modal, Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';

// ✅ Détection plateforme web
const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 1200;

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  amber500: '#F59E0B',
  amber100: '#FEF3C7',
  purple600: '#7C3AED',
  rose500: '#F43F5E',
  rose100: '#FFE4E6',
};

export default function CoachIAScreen() {
  const { conversations, loading, createConversation, deleteConversation, existingSubjectCodes } = useCoachConversations();
  const { profile } = useUserProfile();
  
  // State pour le modal de sélection de matière
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  // State pour le menu de suppression
  const [selectedConversation, setSelectedConversation] = useState<typeof conversations[0] | null>(null);
  const [showDeleteMenu, setShowDeleteMenu] = useState(false);

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return "aujourd'hui";
    if (diffInDays === 1) return "hier";
    if (diffInDays < 7) return `il y a ${diffInDays} jours`;
    if (diffInDays < 30) return `il y a ${Math.floor(diffInDays / 7)} sem.`;
    return `il y a ${Math.floor(diffInDays / 30)} mois`;
  };

  const handleNewConversation = () => {
    // Vérifier que l'utilisateur a des matières configurées
    if (!profile?.subject_codes || profile.subject_codes.length === 0) {
      if (isWeb) {
        window.alert('Tu dois d\'abord sélectionner tes matières dans ton profil.');
      } else {
        Alert.alert(
          'Aucune matière',
          'Tu dois d\'abord sélectionner tes matières dans ton profil.',
          [{ text: 'OK' }]
        );
      }
      return;
    }
    setIsModalVisible(true);
  };

  const handleSelectSubject = async (subject: {
    code: string;
    label: string;
    diploma: string;
    section: string;
  }) => {
    setIsCreating(true);

    try {
      const conversationId = await createConversation({
        subjectCode: subject.code,
        subjectLabel: subject.label,
        diploma: subject.diploma,
        section: subject.section,
      });

      if (conversationId) {
        setIsModalVisible(false);
        
        // Naviguer vers la nouvelle conversation
        router.push({
          pathname: '/conversation/[id]',
          params: {
            id: conversationId,
            title: subject.label,
            diploma: subject.diploma,
            subjectCode: subject.code,
          },
        });
      } else {
        if (isWeb) {
          window.alert('Impossible de créer la conversation. Réessaie.');
        } else {
          Alert.alert(
            'Erreur',
            'Impossible de créer la conversation. Réessaie.',
            [{ text: 'OK' }]
          );
        }
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      if (isWeb) {
        window.alert('Une erreur est survenue. Réessaie.');
      } else {
        Alert.alert(
          'Erreur',
          'Une erreur est survenue. Réessaie.',
          [{ text: 'OK' }]
        );
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleConversationLongPress = (conversation: typeof conversations[0]) => {
    setSelectedConversation(conversation);
    setShowDeleteMenu(true);
  };

  const handleDeleteConversation = () => {
    if (!selectedConversation) return;
    
    if (isWeb) {
      if (window.confirm(`Supprimer "${selectedConversation.subject_label}" ?\n\nTous les messages seront perdus.`)) {
        deleteConversation(selectedConversation.id).then(success => {
          if (!success) {
            window.alert('Impossible de supprimer la conversation.');
          }
          setSelectedConversation(null);
        });
      }
    } else {
      Alert.alert(
        'Supprimer la conversation',
        `Supprimer "${selectedConversation.subject_label}" ?\n\nTous les messages seront perdus.`,
        [
          { text: 'Annuler', style: 'cancel' },
          { 
            text: 'Supprimer', 
            style: 'destructive', 
            onPress: async () => {
              const success = await deleteConversation(selectedConversation.id);
              if (!success) {
                Alert.alert('Erreur', 'Impossible de supprimer la conversation.');
              }
              setSelectedConversation(null);
            }
          },
        ]
      );
    }
    setShowDeleteMenu(false);
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.centered]}>
        <ActivityIndicator size="large" color={colors.indigo600} />
        <Text style={styles.loadingText}>Chargement...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* ✅ Header FIXE avec gradient violet - centré sur web */}
      <View style={styles.headerWrapper}>
        <LinearGradient
          colors={[colors.indigo500, colors.indigo600, colors.indigo700]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.headerFixed}
        >
          <View style={styles.headerInner}>
            <View style={styles.headerIconCircle}>
              <ErnestMascot size={40} enableTapBounce={false} />
            </View>
            <View style={styles.headerTextContainer}>
              <Text style={styles.headerTitle}>Coach Ernest</Text>
              <Text style={styles.headerSubtitle}>Ton coach perso pour réussir</Text>
            </View>
          </View>
        </LinearGradient>
      </View>

      {/* ✅ Contenu scrollable */}
      <ScrollView 
        style={styles.scrollView} 
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* ✅ Wrapper pour limiter la largeur sur web */}
        <View style={styles.innerContainer}>

          {/* Info section */}
          <View style={styles.section}>
            
            {/* Badges row */}
            <View style={styles.infoRow}>
              <View style={styles.subjectBadge}>
                <GraduationCap size={16} color={colors.indigo600} weight="duotone" />
                <Text style={styles.subjectText}>{profile?.diploma || 'BTS'} {profile?.section || ''}</Text>
              </View>
              <Text style={styles.conversationsCount}>
                {conversations.length} conversation{conversations.length > 1 ? 's' : ''}
              </Text>
            </View>

            {/* Info box Ernest */}
            <LinearGradient
              colors={[colors.indigo50, colors.indigo100]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.infoBox}
            >
              <View style={styles.infoBoxIcon}>
                <Sparkle size={20} color={colors.amber500} weight="fill" />
              </View>
              <View style={styles.infoBoxContent}>
                <Text style={styles.infoTitle}>Ernest est prêt à t'aider ! 🔥</Text>
                <Text style={styles.infoSubtitle}>
                  Une conversation = une matière, pour un suivi clair
                </Text>
              </View>
            </LinearGradient>

            {/* CTA Button */}
            <TouchableOpacity
              style={styles.ctaButton}
              onPress={handleNewConversation}
              activeOpacity={0.8}
            >
              <LinearGradient
                colors={[colors.indigo600, colors.purple600]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.ctaButtonGradient}
              >
                <Plus size={22} color={colors.white} weight="bold" />
                <Text style={styles.ctaButtonText}>Nouvelle conversation</Text>
              </LinearGradient>
            </TouchableOpacity>
          </View>

          {/* Liste des conversations */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Mes conversations</Text>
              {conversations.length > 0 && (
                <Text style={styles.sectionHint}>Appui long pour supprimer</Text>
              )}
            </View>
            
            {conversations.length === 0 ? (
              <View style={styles.emptyCard}>
                <ChatCircleDots size={48} color={colors.slate400} weight="duotone" />
                <Text style={styles.emptyTitle}>Aucune conversation</Text>
                <Text style={styles.emptySubtitle}>
                  Commence une nouvelle conversation avec Ernest pour réviser !
                </Text>
              </View>
            ) : (
              <View style={styles.conversationsList}>
                {conversations.map((conv) => (
                  <TouchableOpacity 
                    key={conv.id} 
                    activeOpacity={0.8}
                    onPress={() => router.push({
                      pathname: '/conversation/[id]',
                      params: { 
                        id: conv.id, 
                        title: conv.subject_label,
                        diploma: conv.diploma,
                        subjectCode: conv.subject_code
                      }
                    })}
                    onLongPress={() => handleConversationLongPress(conv)}
                    delayLongPress={500}
                  >
                    <View style={styles.conversationCard}>
                      <LinearGradient
                        colors={[colors.indigo100, colors.indigo50]}
                        style={styles.conversationIcon}
                      >
                        <ChatCircleDots size={24} color={colors.indigo600} weight="duotone" />
                      </LinearGradient>
                      
                      <View style={styles.conversationInfo}>
                        <View style={styles.conversationBadge}>
                          <Text style={styles.conversationBadgeText}>
                            {conv.diploma} {conv.section}
                          </Text>
                        </View>
                        <Text style={styles.conversationTitle} numberOfLines={1}>
                          {conv.subject_label}
                        </Text>
                        <View style={styles.conversationMeta}>
                          <Clock size={14} color={colors.slate400} weight="duotone" />
                          <Text style={styles.conversationTime}>
                            {getRelativeTime(conv.last_message_at)}
                          </Text>
                        </View>
                      </View>
                      
                      <View style={styles.conversationChevron}>
                        <CaretRight size={20} color={colors.slate400} weight="bold" />
                      </View>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          <View style={{ height: 120 }} />
        </View>
      </ScrollView>

      {/* Modal de sélection de matière */}
      <SelectSubjectModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onSelect={handleSelectSubject}
        subjectCodes={profile?.subject_codes || []}
        isLoading={isCreating}
        existingSubjectCodes={existingSubjectCodes}
      />

      {/* Menu de suppression */}
      <Modal
        visible={showDeleteMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDeleteMenu(false)}
      >
        <TouchableOpacity 
          style={styles.menuOverlay}
          activeOpacity={1}
          onPress={() => setShowDeleteMenu(false)}
        >
          <View style={styles.menuContainer}>
            <View style={styles.menuHandle} />
            <Text style={styles.menuTitle} numberOfLines={1}>
              {selectedConversation?.subject_label || 'Conversation'}
            </Text>
            
            <TouchableOpacity 
              style={styles.menuOption}
              onPress={handleDeleteConversation}
              activeOpacity={0.7}
            >
              <View style={styles.menuOptionIconDelete}>
                <Trash size={22} color={colors.rose500} weight="duotone" />
              </View>
              <Text style={styles.menuOptionTextDelete}>Supprimer la conversation</Text>
              <CaretRight size={18} color={colors.slate400} weight="bold" />
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.menuCancel}
              onPress={() => setShowDeleteMenu(false)}
              activeOpacity={0.7}
            >
              <Text style={styles.menuCancelText}>Annuler</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  // ✅ Header centré sur web avec fond gris visible sur les côtés
  headerWrapper: {
    backgroundColor: colors.slate50,
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  // ✅ Header avec minHeight et contenu centré verticalement sur web
  headerFixed: {
    paddingTop: isWeb ? 0 : 64,
    paddingBottom: isWeb ? 0 : 32,
    paddingHorizontal: 20,
    borderBottomLeftRadius: 32,
    borderBottomRightRadius: 32,
    zIndex: 10,
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    ...(isWeb && {
      minHeight: 160,
      justifyContent: 'center',
      paddingBottom: 15,
    }),
  },
  headerInner: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerIconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  headerTextContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.white,
    letterSpacing: -0.5,
    marginBottom: 6,
  },
  headerSubtitle: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.9)',
    fontWeight: '500',
  },
  // ✅ ScrollView sous le header fixe
  scrollView: {
    flex: 1,
  },
  // ✅ Centrage sur web
  contentContainer: {
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  // ✅ Wrapper avec largeur max
  innerContainer: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    alignSelf: 'center',
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    color: colors.slate500,
    fontSize: 16,
  },
  
  // Section
  section: {
    paddingHorizontal: 20,
    paddingTop: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.slate900,
    letterSpacing: -0.3,
  },
  sectionHint: {
    fontSize: 13,
    color: colors.slate400,
  },
  
  // Info Row
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  subjectBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.white,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.slate200,
  },
  subjectText: {
    color: colors.slate900,
    fontSize: 14,
    fontWeight: '600',
  },
  conversationsCount: {
    color: colors.slate500,
    fontSize: 14,
  },
  
  // Info Box
  infoBox: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 20,
    marginBottom: 20,
    gap: 14,
    borderWidth: 1,
    borderColor: colors.indigo100,
  },
  infoBoxIcon: {
    width: 44,
    height: 44,
    borderRadius: 14,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoBoxContent: {
    flex: 1,
  },
  infoTitle: {
    color: colors.slate900,
    fontWeight: '700',
    fontSize: 16,
    marginBottom: 4,
  },
  infoSubtitle: {
    color: colors.slate600,
    fontSize: 14,
    lineHeight: 20,
  },
  
  // CTA Button
  ctaButton: {
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: colors.indigo600,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 4,
  },
  ctaButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 18,
    gap: 10,
  },
  ctaButtonText: {
    color: colors.white,
    fontSize: 17,
    fontWeight: '700',
  },
  
  // Empty state
  emptyCard: {
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 40,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: colors.slate200,
    borderStyle: 'dashed',
  },
  emptyTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: colors.slate900,
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    color: colors.slate500,
    textAlign: 'center',
    lineHeight: 20,
  },
  
  // Conversations List
  conversationsList: {
    gap: 12,
  },
  conversationCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 16,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 3,
  },
  conversationIcon: {
    width: 52,
    height: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  conversationInfo: {
    flex: 1,
    gap: 6,
  },
  conversationBadge: {
    alignSelf: 'flex-start',
    backgroundColor: colors.indigo100,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
  },
  conversationBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.indigo600,
  },
  conversationTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate900,
  },
  conversationMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  conversationTime: {
    fontSize: 13,
    color: colors.slate400,
  },
  conversationChevron: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.slate100,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Menu de suppression
  menuOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  menuContainer: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    padding: 24,
    paddingBottom: 40,
  },
  menuHandle: {
    width: 40,
    height: 4,
    backgroundColor: colors.slate300,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 20,
  },
  menuTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate500,
    textAlign: 'center',
    marginBottom: 20,
  },
  menuOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 16,
    backgroundColor: colors.slate50,
    marginBottom: 10,
  },
  menuOptionIconDelete: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: colors.rose100,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  menuOptionTextDelete: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: colors.rose500,
  },
  menuCancel: {
    padding: 16,
    alignItems: 'center',
    marginTop: 8,
    backgroundColor: colors.slate100,
    borderRadius: 14,
  },
  menuCancelText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate600,
  },
});